import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  Loader2,
  Wallet,
  Banknote,
  FileSpreadsheet,
  CheckCheck,
  Pencil,
  ArrowDownCircle,
  ArrowUpCircle,
  Equal,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import SubmitReimbursementDialog from "@/components/SubmitReimbursementDialog";
import SettleOPADialog from "@/components/SettleOPADialog";
import EditSettlementDialog from "@/components/EditSettlementDialog";
import ReleasePaymentDialog from "@/components/ReleasePaymentDialog";
import TaskTicketLightbox from "@/components/TaskTicketLightbox";
import OPATimelineDialog from "@/components/OPATimelineDialog";

function fmtINR(n) {
  if (n == null || isNaN(n)) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n);
}
function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
}

const TABS = [
  { key: "payment_pending", label: "Payment Pending", color: "text-amber-300", bg: "bg-amber-500/15" },
  { key: "payment_done", label: "Payment Done", color: "text-emerald-300", bg: "bg-emerald-500/15" },
  { key: "reimbursed", label: "Reimbursement Form Submitted", color: "text-violet-300", bg: "bg-violet-500/15" },
  { key: "settled", label: "Settled", color: "text-cyan-300", bg: "bg-cyan-500/15" },
];

function LinkedCell({ opa, onPreview }) {
  if (opa.task_id) {
    return (
      <button
        type="button"
        onClick={() => onPreview({ type: "task", id: opa.task_id })}
        className="font-mono text-sky-400 hover:underline"
        data-testid={`opa-link-task-${opa.id}`}
      >
        {opa.task_code || "Task"}
      </button>
    );
  }
  if (opa.issue_id) {
    return (
      <button
        type="button"
        onClick={() => onPreview({ type: "ticket", id: opa.issue_id })}
        className="font-mono text-amber-400 hover:underline"
        data-testid={`opa-link-ticket-${opa.id}`}
      >
        {opa.issue_code || "Ticket"}
      </button>
    );
  }
  return <span className="text-zinc-600">—</span>;
}

function DeltaPill({ settlement }) {
  if (!settlement) return null;
  const delta = settlement.delta || 0;
  const abs = Math.abs(delta);
  if (settlement.direction === "pay") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/20 font-mono">
        <ArrowUpCircle className="w-3 h-3" /> Paid {fmtINR(abs)}
      </span>
    );
  }
  if (settlement.direction === "collect") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/20 font-mono">
        <ArrowDownCircle className="w-3 h-3" /> Collected {fmtINR(abs)}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-zinc-700/30 text-zinc-300 border border-white/10 font-mono">
      <Equal className="w-3 h-3" /> Even
    </span>
  );
}

export default function OPAHandlePage() {
  const { user } = useAuth();
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("payment_pending");

  // Dialog state
  const [submitOpa, setSubmitOpa] = useState(null);
  const [settleOpa, setSettleOpa] = useState(null);
  const [editOpa, setEditOpa] = useState(null);
  const [releaseOpa, setReleaseOpa] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [timelineOpa, setTimelineOpa] = useState(null);

  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/opa-requests", { params: { status: "approved" } });
      setAll(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const buckets = useMemo(() => {
    const m = { payment_pending: [], payment_done: [], reimbursed: [], settled: [] };
    for (const o of all) {
      if (m[o.lifecycle]) m[o.lifecycle].push(o);
    }
    return m;
  }, [all]);

  const handleAfterUpdate = (updated) => {
    // Always recompute lifecycle from the merged doc — never trust a stale
    // value from the previous state.
    setAll((prev) => prev.map((o) => {
      if (o.id !== updated.id) return o;
      const merged = { ...o, ...updated };
      const lc = updated.lifecycle || (merged.settlement ? "settled" : merged.reimbursement ? "reimbursed" : merged.released_at ? "payment_done" : "payment_pending");
      return { ...merged, lifecycle: lc };
    }));
    const lc = updated.lifecycle || (updated.settlement ? "settled" : updated.reimbursement ? "reimbursed" : updated.released_at ? "payment_done" : "payment_pending");
    setTab(lc);
  };

  return (
    <div className="space-y-6" data-testid="opa-handle-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-white flex items-center gap-3" style={{ fontFamily: "Outfit" }}>
            <Wallet className="w-7 h-7 text-emerald-400" /> OPA Handle
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Track Operational Advances through payment, reimbursement and settlement.</p>
        </div>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-zinc-900 border border-white/10 flex-wrap h-auto">
          {TABS.map((t) => (
            <TabsTrigger
              key={t.key}
              value={t.key}
              className="data-[state=active]:bg-white data-[state=active]:text-black"
              data-testid={`opa-tab-${t.key}`}
            >
              {t.label}
              <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${t.bg} ${t.color}`}>{buckets[t.key].length}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {TABS.map((t) => (
          <TabsContent key={t.key} value={t.key} className="mt-4">
            <OPATable
              rows={buckets[t.key]}
              loading={loading}
              tab={t.key}
              isAdmin={isAdmin}
              onSubmitReimbursement={setSubmitOpa}
              onSettle={setSettleOpa}
              onEdit={setEditOpa}
              onRelease={setReleaseOpa}
              onPreview={setPreviewing}
              onOpenTimeline={setTimelineOpa}
            />
          </TabsContent>
        ))}
      </Tabs>

      <SubmitReimbursementDialog opa={submitOpa} onClose={() => setSubmitOpa(null)} onSubmitted={handleAfterUpdate} />
      <SettleOPADialog opa={settleOpa} onClose={() => setSettleOpa(null)} onSettled={handleAfterUpdate} />
      <EditSettlementDialog opa={editOpa} onClose={() => setEditOpa(null)} onSaved={handleAfterUpdate} />
      <ReleasePaymentDialog opa={releaseOpa} onClose={() => setReleaseOpa(null)} onReleased={handleAfterUpdate} />
      <TaskTicketLightbox open={previewing} onClose={() => setPreviewing(null)} />
      <OPATimelineDialog opa={timelineOpa} onClose={() => setTimelineOpa(null)} />
    </div>
  );
}

function OPATable({ rows, loading, tab, isAdmin, onSubmitReimbursement, onSettle, onEdit, onRelease, onPreview, onOpenTimeline }) {
  const columns = ["OPA ID", "Task / Ticket", "Purpose", "Approved Amount", ...(tab === "payment_pending" ? [] : ["Reimbursement"]), ...(tab === "settled" ? ["Delta", "Settled On"] : []), "Status", "Action"];

  return (
    <div className="w-full overflow-hidden border border-white/10 rounded-lg bg-zinc-900">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-zinc-950/50 border-b border-white/10 text-left">
            {columns.map((c, i) => (
              <th key={i} className="px-4 py-3 text-[11px] uppercase tracking-wider text-zinc-500 font-medium">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-zinc-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading…
            </td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={columns.length} className="px-4 py-12 text-center text-zinc-500 text-sm">No items in this tab.</td></tr>
          ) : (
            rows.map((o) => (
              <tr key={o.id} className="border-b border-white/5 hover:bg-white/[0.025] transition-colors" data-testid={`opa-row-${o.id}`}>
                <td className="px-4 py-3 font-mono text-xs">
                  <button
                    type="button"
                    onClick={() => onOpenTimeline(o)}
                    className="text-emerald-400 hover:underline cursor-pointer"
                    data-testid={`opa-id-${o.id}`}
                    title="View OPA timeline"
                  >
                    {o.code}
                  </button>
                </td>
                <td className="px-4 py-3 text-xs"><LinkedCell opa={o} onPreview={onPreview} /></td>
                <td className="px-4 py-3 text-zinc-300 max-w-[240px] truncate" title={o.purpose || ""}>{o.purpose || "—"}</td>
                <td className="px-4 py-3 font-mono text-white">{fmtINR(o.amount)}</td>
                {tab !== "payment_pending" && (
                  <td className="px-4 py-3 text-xs">
                    {o.reimbursement ? (
                      <span className="font-mono">
                        <span className="text-violet-300">{o.reimbursement.form_number}</span>
                        <span className="text-zinc-500"> · </span>
                        <span className="text-white">{fmtINR(o.reimbursement.total_value)}</span>
                      </span>
                    ) : (
                      <span className="text-zinc-600">—</span>
                    )}
                  </td>
                )}
                {tab === "settled" && (
                  <>
                    <td className="px-4 py-3 text-xs"><DeltaPill settlement={o.settlement} /></td>
                    <td className="px-4 py-3 text-zinc-500 text-xs">{fmtDate(o.settlement?.date)}</td>
                  </>
                )}
                <td className="px-4 py-3 whitespace-nowrap">
                  {tab === "payment_pending" && <span className="text-[11px] uppercase tracking-wider text-amber-400">Payment Pending</span>}
                  {tab === "payment_done" && <span className="text-[11px] uppercase tracking-wider text-emerald-400">Payment Done</span>}
                  {tab === "reimbursed" && <span className="text-[11px] uppercase tracking-wider text-violet-400">Form Submitted</span>}
                  {tab === "settled" && <span className="text-[11px] uppercase tracking-wider text-cyan-400">Settled</span>}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {tab === "payment_pending" && isAdmin && (
                    <button
                      onClick={() => onRelease(o)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs text-emerald-300 hover:bg-emerald-500/10 border border-emerald-500/30"
                      data-testid={`opa-release-${o.id}`}
                    >
                      <Banknote className="w-3.5 h-3.5" /> Release
                    </button>
                  )}
                  {tab === "payment_done" && isAdmin && (
                    <button
                      onClick={() => onSubmitReimbursement(o)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs text-violet-300 hover:bg-violet-500/10 border border-violet-500/30"
                      data-testid={`opa-submit-reimb-${o.id}`}
                    >
                      <FileSpreadsheet className="w-3.5 h-3.5" /> Submit Reimbursement Form
                    </button>
                  )}
                  {tab === "reimbursed" && isAdmin && (
                    <button
                      onClick={() => onSettle(o)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs text-cyan-300 hover:bg-cyan-500/10 border border-cyan-500/30"
                      data-testid={`opa-settle-${o.id}`}
                    >
                      <CheckCheck className="w-3.5 h-3.5" /> Settle
                    </button>
                  )}
                  {tab === "settled" && isAdmin && (
                    <button
                      onClick={() => onEdit(o)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs text-zinc-300 hover:bg-white/10 border border-white/10"
                      data-testid={`opa-edit-${o.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
